import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from './baseQuery';
import {
  ChargingStation,
  ScooterBattery,
  ScooterType,
  BatteryHistory,
  BatteryStats,
  CreateChargingStationBody,
  UpdateChargingStationBody,
  CreateBatteryBody,
  UpdateBatteryBody,
  CreateBatteryHistoryBody,
  GetBatteriesParams,
  GetBatteryHistoryParams
} from '../../resources/types/batteriesTypes';

export const batteriesApi = createApi({
  reducerPath: 'batteriesApi',
  baseQuery,
  tagTypes: ['ChargingStations', 'Batteries', 'BatteryHistory', 'BatteryStats', 'ScooterTypes'],
  endpoints: builder => ({
    getScooterTypes: builder.query<{ data: ScooterType[] }, void>({
      query: () => '/scooter/scooter_types',
      providesTags: ['ScooterTypes']
    }),

    getChargingStations: builder.query<{ data: ChargingStation[] }, void>({
      query: () => '/scooter_batteries/charging_stations',
      providesTags: ['ChargingStations']
    }),
    
    createChargingStation: builder.mutation<{ data: ChargingStation }, CreateChargingStationBody>({
      query: body => ({
        url: '/scooter_batteries/charging_stations',
        method: 'POST',
        body
      }),
      invalidatesTags: ['ChargingStations']
    }),
    
    updateChargingStation: builder.mutation<{ data: ChargingStation }, { id: string; body: UpdateChargingStationBody }>({
      query: ({ id, body }) => ({
        url: `/scooter_batteries/charging_stations/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['ChargingStations']
    }),
    
    deleteChargingStation: builder.mutation<{ success: boolean }, string>({
      query: id => ({
        url: `/scooter_batteries/charging_stations/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ChargingStations']
    }),
    
    getBatteries: builder.query<{ data: ScooterBattery[]; pagination: any }, GetBatteriesParams>({
      query: params => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
          }
        });
        return `/scooter_batteries/batteries?${queryParams.toString()}`;
      },
      providesTags: ['Batteries']
    }),
    
    getBatteriesForEfficiency: builder.query<{ data: ScooterBattery[] }, void>({
      query: () => '/scooter_batteries/batteries/efficiency',
      providesTags: ['Batteries']
    }),
    
    getBatteryStats: builder.query<BatteryStats, void>({
      query: () => '/scooter_batteries/batteries/stats',
      providesTags: ['BatteryStats']
    }),
    
    createBattery: builder.mutation<{ data: ScooterBattery }, CreateBatteryBody>({
      query: body => ({
        url: '/scooter_batteries/batteries',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Batteries', 'BatteryStats']
    }),
    
    updateBattery: builder.mutation<{ data: ScooterBattery }, { id: string; body: UpdateBatteryBody }>({
      query: ({ id, body }) => ({
        url: `/scooter_batteries/batteries/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['Batteries', 'BatteryStats']
    }),
    
    deleteBattery: builder.mutation<{ success: boolean }, string>({
      query: id => ({
        url: `/scooter_batteries/batteries/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Batteries', 'BatteryStats']
    }),
    
    getBatteryHistory: builder.query<{ data: BatteryHistory[] }, GetBatteryHistoryParams>({
      query: params => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
        return `/scooter_batteries/batteries/history?${queryParams.toString()}`;
      },
      providesTags: ['BatteryHistory']
    }),
    
    createBatteryHistory: builder.mutation<{ data: BatteryHistory }, CreateBatteryHistoryBody>({
      query: body => ({
        url: '/scooter_batteries/batteries/history',
        method: 'POST',
        body
      }),
      invalidatesTags: ['BatteryHistory', 'Batteries']
    })
  })
});

export const {
  useGetScooterTypesQuery,
  useGetChargingStationsQuery,
  useCreateChargingStationMutation,
  useUpdateChargingStationMutation,
  useDeleteChargingStationMutation,
  useGetBatteriesQuery,
  useLazyGetBatteriesQuery,
  useGetBatteriesForEfficiencyQuery,
  useGetBatteryStatsQuery,
  useCreateBatteryMutation,
  useUpdateBatteryMutation,
  useDeleteBatteryMutation,
  useGetBatteryHistoryQuery,
  useCreateBatteryHistoryMutation
} = batteriesApi;
